import React from 'react';
import PropTypes from 'prop-types';
import {Skeleton} from 'antd';
import './Skeletons.css';

/**
 * Loading placeholders shaped like the real content they stand in for, so a
 * list that is still loading keeps its final size and nothing jumps when the
 * data arrives. Built on antd's Skeleton.Node (same shimmer as the rest of
 * antd) with explicit sizes.
 */
export const SkeletonBar = ({width = '100%', height = 12, radius = 4, className}) => (
  <Skeleton.Node
    active
    className={className}
    styles={{
      root: {display: 'block', width},
      content: {width: '100%', height, borderRadius: radius},
    }}
  />
);

SkeletonBar.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.number,
  radius: PropTypes.number,
  className: PropTypes.string,
};

/** Home › Recent projects: one card (thumbnail + name + time), same box as .hm-project. */
export const ProjectCardSkeleton = () => (
  <div className="hm-project hm-project-skeleton" data-testid="project-card-skeleton">
    <SkeletonBar className="hm-project-skeleton-thumb" height={110} radius={0} />
    <span className="hm-project-info">
      <span className="skel-line skel-line-16"><SkeletonBar height={14} width="60%" /></span>
      <span className="skel-line skel-line-13"><SkeletonBar height={11} width="30%" /></span>
    </span>
  </div>
);

/** Home › the "Continue" box in the header (last session + Resume), same box as .hm-continue. */
export const ContinueSkeleton = () => (
  <div aria-busy="true" aria-label="Loading your last session" className="hm-continue hm-continue-skeleton">
    <SkeletonBar height={34} radius={8} width={34} />
    <span className="hm-continue-skeleton-text">
      <span className="skel-line skel-line-14"><SkeletonBar height={12} width={80} /></span>
      <span className="skel-line skel-line-13"><SkeletonBar height={11} width={170} /></span>
    </span>
    <SkeletonBar height={34} radius={8} width={96} />
  </div>
);

const ROW_WIDTHS = ['62%', '48%', '55%'];

/** Home › Recent sessions / Favorites: rows shaped like .hm-row (name left, time right). */
export const ListRowsSkeleton = ({rows = 3, label = 'Loading'}) => (
  <div aria-busy="true" aria-label={label} className="hm-rows-skeleton">
    {Array.from({length: rows}, (_, i) => (
      <div className="hm-row hm-row-skeleton" data-testid="list-row-skeleton" key={i}>
        <span className="skel-line skel-line-14"><SkeletonBar height={12} width={ROW_WIDTHS[i % ROW_WIDTHS.length]} /></span>
        <span className="skel-line skel-line-13 hm-row-skeleton-time"><SkeletonBar height={11} width={52} /></span>
      </div>
    ))}
  </div>
);

ListRowsSkeleton.propTypes = {
  rows: PropTypes.number,
  label: PropTypes.string,
};

/** History › Kanban: one card shaped like .session-kanban-card (name, then the time/actions footer). */
export const KanbanCardSkeleton = ({nameWidth = '70%'}) => (
  <div className="session-kanban-card session-kanban-card-skeleton" data-testid="kanban-card-skeleton">
    <span className="skel-line skel-line-13"><SkeletonBar height={12} width={nameWidth} /></span>
    <span className="skel-line skel-kanban-footer skel-gap-8"><SkeletonBar height={10} width={48} /></span>
  </div>
);

KanbanCardSkeleton.propTypes = {
  nameWidth: PropTypes.string,
};

